import { cloneElement, forwardRef } from "react";

import { useDelayedLoading } from "@oneleet/utils/src/hooks";
import {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithoutRef,
} from "@oneleet/utils/src/polymorphism";

import { type VariantProps, cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

import { Loader } from "@oneleet/components/src/atoms";

const DEFAULT_SIZE = "medium";
const DEFAULT_IS_DISABLED = false;

const button = cva(
  "flex h-min w-min shrink-0 select-none items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md text-sm font-semibold",
  {
    variants: {
      variant: {
        solid: "",
        soft: "",
        outlined: "",
        dashed: "",
        plain: "",
      },
      color: {
        brand: "",
        gray: "",
        red: "",
        green: "",
      },
      size: {
        tiny: "px-2 py-1 text-xs",
        small: "px-2 py-1",
        medium: "px-2.5 py-1.5",
        large: "px-3 py-2",
        huge: "px-3.5 py-2.5",
      },
      disabled: {
        true: "cursor-default opacity-50",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "solid",
        color: "brand",
        className:
          "bg-brand-500 hover:data-[button-disabled=false]:bg-brand-600 text-white",
      },
      {
        variant: "soft",
        color: "brand",
        className:
          "bg-brand-50 text-brand-500 hover:data-[button-disabled=false]:bg-brand-100",
      },
      {
        variant: "outlined",
        color: "brand",
        className:
          "text-brand-500 hover:data-[button-disabled=false]:bg-brand-50 ring-brand-300 bg-transparent ring-1 ring-inset",
      },
      {
        variant: "dashed",
        color: "brand",
        className:
          "text-brand-500 hover:data-[button-disabled=false]:bg-brand-50 border-brand-300 border-2 border-dashed bg-transparent",
      },
      {
        variant: "plain",
        color: "brand",
        className:
          "text-brand-500 hover:data-[button-disabled=false]:bg-brand-50 bg-transparent",
      },
      {
        variant: "solid",
        color: "gray",
        className:
          "bg-gray-500 text-white hover:data-[button-disabled=false]:bg-gray-400",
      },
      {
        variant: "soft",
        color: "gray",
        className:
          "bg-gray-100 text-gray-600 hover:data-[button-disabled=false]:bg-gray-200",
      },
      {
        variant: "outlined",
        color: "gray",
        className:
          "bg-transparent text-gray-600 ring-1 ring-inset ring-gray-300 hover:data-[button-disabled=false]:bg-gray-100",
      },
      {
        variant: "dashed",
        color: "gray",
        className:
          "border-2 border-dashed border-gray-300 bg-transparent text-gray-600 hover:data-[button-disabled=false]:bg-gray-100",
      },
      {
        variant: "plain",
        color: "gray",
        className:
          "bg-transparent text-gray-600 hover:data-[button-disabled=false]:bg-gray-100",
      },
      {
        variant: "solid",
        color: "red",
        className:
          "bg-red-600 text-white hover:data-[button-disabled=false]:bg-red-500",
      },
      {
        variant: "soft",
        color: "red",
        className:
          "bg-red-50 text-red-600 hover:data-[button-disabled=false]:bg-red-100",
      },
      {
        variant: "outlined",
        color: "red",
        className:
          "bg-transparent text-red-600 ring-1 ring-inset ring-red-300 hover:data-[button-disabled=false]:bg-red-50",
      },
      {
        variant: "dashed",
        color: "red",
        className:
          "border-2 border-dashed border-red-300 bg-transparent text-red-600 hover:data-[button-disabled=false]:bg-red-50",
      },
      {
        variant: "plain",
        color: "red",
        className:
          "bg-transparent text-red-600 hover:data-[button-disabled=false]:bg-red-50",
      },
      {
        variant: "solid",
        color: "green",
        className:
          "bg-green-500 text-white hover:data-[button-disabled=false]:bg-green-400",
      },
      {
        variant: "soft",
        color: "green",
        className:
          "bg-green-50 text-green-600 hover:data-[button-disabled=false]:bg-green-100",
      },
      {
        variant: "outlined",
        color: "green",
        className:
          "bg-transparent text-green-600 ring-1 ring-inset ring-green-300 hover:data-[button-disabled=false]:bg-green-50",
      },
      {
        variant: "dashed",
        color: "green",
        className:
          "border-2 border-dashed border-green-300 bg-transparent text-green-600 hover:data-[button-disabled=false]:bg-green-50",
      },
      {
        variant: "plain",
        color: "green",
        className:
          "bg-transparent text-green-600 hover:data-[button-disabled=false]:bg-green-50",
      },
    ],
    defaultVariants: {
      /* 
      Avoid adding variant and color defaults to require developers to make a conscious decision about which variant and color 
      is most appropriate for the current use case, based on our Button guidelines in Storybook.
      */
      size: DEFAULT_SIZE,
      disabled: DEFAULT_IS_DISABLED,
    },
  },
);

const icon = cva("h-5 w-5 shrink-0", {
  variants: {
    variant: {
      solid: "",
      soft: "",
      outlined: "",
      dashed: "",
      plain: "",
    },
    size: {
      tiny: "h-4 w-4",
      small: "",
      medium: "",
      large: "",
      huge: "",
    },
    color: {
      brand: "",
      gray: "",
      red: "",
      green: "",
    },
  },
});

const loader = cva("h-4 w-4", {
  variants: {
    variant: {
      solid: "",
      soft: "",
      outlined: "",
      dashed: "",
      plain: "",
    },
    color: {
      brand: "",
      gray: "",
      red: "",
      green: "",
    },
    size: {
      tiny: "h-3 w-3",
      small: "",
      medium: "",
      large: "",
      huge: "",
    },
  },
  compoundVariants: [
    {
      variant: "solid",
      color: "brand",
      className: "text-brand-300 fill-white",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "brand",
      className: "text-brand-300 fill-brand-500",
    },
    {
      variant: "solid",
      color: "gray",
      className: "fill-white text-gray-300",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "gray",
      className: "fill-gray-600 text-gray-300",
    },
    {
      variant: "solid",
      color: "red",
      className: "fill-white text-red-300",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "red",
      className: "fill-red-600 text-red-300",
    },
    {
      variant: "solid",
      color: "green",
      className: "fill-white text-green-300",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "green",
      className: "fill-green-600 text-green-300",
    },
  ],
});

export type IconProps = {
  className?: string;
};

type ButtonVariantProps = VariantProps<typeof button>;

type RequiredButtonVariantProps = Omit<
  ButtonVariantProps,
  "variant" | "color"
> &
  Required<Pick<ButtonVariantProps, "variant" | "color">>;

export type ButtonProps = RequiredButtonVariantProps & {
  label: string;
  startIcon?: React.ReactElement<IconProps>;
  startDecorator?: React.ReactNode;
  endIcon?: React.ReactElement<IconProps>;
  endDecorator?: React.ReactNode;
  type?: "button" | "submit" | "reset";
  isLoading?: boolean;
  loadingDelay?: number;
  loadingLabel?: string;
  loaderSide?: "start" | "end";
  className?: string;
  classNames?: {
    root?: string;
    label?: string;
    loader?: string;
  };
  onClick?: (event: React.MouseEvent) => void;
  name?: string;
};

type PolymorphicButton = PolymorphicForwardRefExoticComponent<
  ButtonProps,
  "button"
>;

const Button: PolymorphicButton = forwardRef(
  <T extends React.ElementType = "button">(
    {
      as,
      label,
      disabled = DEFAULT_IS_DISABLED,
      variant,
      color,
      size = DEFAULT_SIZE,
      startIcon,
      startDecorator,
      endIcon,
      endDecorator,
      type = "button",
      isLoading = false,
      loadingDelay,
      loadingLabel,
      loaderSide = "end",
      className,
      classNames,
      onClick,
      ...rest
    }: PolymorphicPropsWithoutRef<ButtonProps, T>,
    ref?: React.ForwardedRef<Element>,
  ) => {
    const Component: React.ElementType = as ?? "button";

    const styledStartIcon = createStyledIcon(
      startIcon,
      size,
      variant,
      color,
      "-ml-0.5",
    );
    const styledEndIcon = createStyledIcon(
      endIcon,
      size,
      variant,
      color,
      "-mr-0.5",
    );

    const showLoading = useDelayedLoading({ isLoading, delay: loadingDelay });

    const isDisabled = disabled || showLoading;

    function renderLoader() {
      return (
        <Loader
          classNames={{
            svg: twMerge(
              loader({ variant, color, size, className: classNames?.loader }),
            ),
          }}
        />
      );
    }

    return (
      <Component
        type={type}
        className={twMerge(
          button({
            variant,
            color,
            size,
            disabled: isDisabled,
            className,
          }),
          classNames?.root,
        )}
        disabled={isDisabled}
        onClick={(event: React.MouseEvent) => {
          // Prevents link buttons from navigating when the button is disabled
          // We do this instead of setting pointer-events-none in the variant styles
          // as pointer-events-none breaks tooltips and other interactions with components that wrap the button
          if (isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClick?.(event);
        }}
        // Prefix data attribute with "button-" to avoid conflicts with other components like DropdownMenu
        data-button-disabled={isDisabled}
        ref={ref}
        {...rest}
      >
        {showLoading && loaderSide === "start" ? null : styledStartIcon}
        {showLoading && loaderSide === "start" ? null : startDecorator}
        {showLoading && loaderSide === "start" && renderLoader()}
        <span className={classNames?.label}>
          {showLoading && loadingLabel ? loadingLabel : label}
        </span>
        {showLoading && loaderSide === "end" ? null : styledEndIcon}
        {showLoading && loaderSide === "end" ? null : endDecorator}
        {showLoading && loaderSide === "end" && renderLoader()}
      </Component>
    );
  },
);

Button.displayName = "Button";

const createStyledIcon = (
  iconElement: React.ReactElement<IconProps> | null | undefined,
  size: "tiny" | "small" | "medium" | "large" | "huge" | null,
  variant: VariantProps<typeof icon>["variant"],
  color: VariantProps<typeof icon>["color"],
  additionalClasses: string = "",
) => {
  if (!iconElement || !size) return null;
  return icon
    ? cloneElement(iconElement, {
        className: twMerge(
          icon({ size, variant, color }),
          additionalClasses,
          iconElement.props.className,
        ),
      })
    : null;
};

export default Button;
